<template>
  <v-container class="pt-2 px-0" fluid>
    <v-layout wrap>
      <v-flex xs12>
        <v-data-table
          :headers="cmeHeaders"
          :items="myRun.config.heliospheric_model.cmes"
          :pagination.sync="cmePagination"
          must-sort
        >
          <template slot="items" slot-scope="props">
            <td
              :style="{cursor: 'pointer'}"
              @click="editCme(props.item)"
              class="text-no-wrap"
            >{{ props.item.datetime | moment("YYYY-MM-DD HH:mm") }}</td>
            <td
              :style="{cursor: 'pointer'}"
              @click="editCme(props.item)"
            >{{ cmeModels[cmeModels.findIndex((model) => model.value === props.item.model)].text }}</td>
            <td :style="{cursor: 'pointer'}" @click="editCme(props.item)">{{ props.item.speed }}</td>
            <td :style="{cursor: 'pointer'}" @click="editCme(props.item)">{{ props.item.latitude }}</td>
            <td :style="{cursor: 'pointer'}" @click="editCme(props.item)">{{ props.item.longitude }}</td>
            <td class="text-xs-right">
              <v-icon @click="editCme(props.item)" class="mr-2" small>edit</v-icon>
              <v-icon @click="deleteCme(props.item)" small>delete</v-icon>
            </td>
          </template>
          <template slot="actions-append">
            <v-speed-dial v-model="speedDialAddCme">
              <template v-slot:activator>
                <v-tooltip left>
                  <template v-slot:activator="{ on }">
                    <v-btn fab icon v-model="speedDialAddCme" v-on="on">
                      <v-icon>add</v-icon>
                      <v-icon>close</v-icon>
                    </v-btn>
                  </template>
                  <span>Add CME</span>
                </v-tooltip>
              </template>
              <v-tooltip left>
                <v-btn @click="createCme" color="success" dark fab slot="activator" small v-on="on">
                  <v-icon>add</v-icon>
                </v-btn>
                <span>Add CME manually</span>
              </v-tooltip>

              <v-tooltip left>
                <v-btn @click="dialogDonki=true" color="info" dark fab slot="activator" small>
                  <v-icon>cloud_download</v-icon>
                </v-btn>
                <span>Add CME from DONKI database</span>
              </v-tooltip>
            </v-speed-dial>
            <v-dialog :fullscreen="$vuetify.breakpoint.smAndDown" max-width="600px" v-model="dialogCme">
              <v-card>
                <v-card-title class="px-3">
                  <span class="headline">CME configuration</span>
                </v-card-title>

                <v-card-text>
                  <v-container class="pa-0" grid-list-md>
                    <v-layout wrap>
                      <v-flex class="px-2" md6 xs12>
                        <v-select :items="cmeModels" @change="modifyCmeModel" label="Model" v-model="cmeConfig.model"></v-select>
                      </v-flex>
                      <v-flex class="px-2" md6 xs12>
                        <v-dialog full-width persistent ref="dialog-cme-date" v-model="modalCmeDate" width="290px">
                          <v-text-field label="Date" readonly slot="activator" v-model="cmeDate"></v-text-field>
                          <v-date-picker @input="$refs['dialog-cme-date'].save(cmeDate)" scrollable v-model="cmeDate"></v-date-picker>
                        </v-dialog>
                      </v-flex>
                      <v-flex class="px-2" md6 xs12>
                        <v-dialog full-width persistent ref="dialog-cme-time" v-model="modalCmeTime" width="290px">
                          <v-text-field label="Time" readonly slot="activator" v-model="cmeTime"></v-text-field>
                          <v-time-picker
                            @click:minute="$refs['dialog-cme-time'].save(cmeTime)"
                            format="24hr"
                            full-width
                            v-model="cmeTime"
                          ></v-time-picker>
                        </v-dialog>
                      </v-flex>
                      <v-flex class="px-2" md6 xs12>
                        <v-text-field
                          :error-messages="errorMessages($v.cmeConfig.speed)"
                          @blur="$v.cmeConfig.speed.$touch()"
                          @input="$v.cmeConfig.speed.$touch()"
                          hint="Propagation speed"
                          label="Speed"
                          suffix="km/s"
                          type="number"
                          v-model="cmeConfig.speed"
                        ></v-text-field>
                      </v-flex>
                      <v-flex class="px-2" md6 xs12>
                        <v-text-field
                          :error-messages="errorMessages($v.cmeConfig.latitude)"
                          @blur="$v.cmeConfig.latitude.$touch()"
                          @input="$v.cmeConfig.latitude.$touch()"
                          hint="Latitudinal orientation"
                          label="Latitude"
                          suffix="deg"
                          type="number"
                          v-model="cmeConfig.latitude"
                        ></v-text-field>
                      </v-flex>
                      <v-flex class="px-2" md6 xs12>
                        <v-text-field
                          :error-messages="errorMessages($v.cmeConfig.longitude)"
                          @blur="$v.cmeConfig.longitude.$touch()"
                          @input="$v.cmeConfig.longitude.$touch()"
                          hint="Longitudinal orientation"
                          label="Longitude"
                          suffix="deg"
                          type="number"
                          v-model="cmeConfig.longitude"
                        ></v-text-field>
                      </v-flex>
                      <v-flex class="px-2" md6 v-if="cmeConfig.model === 'FRi3D'" xs12>
                        <v-text-field
                          :error-messages="errorMessages($v.cmeConfig.toroidalHeight)"
                          @blur="$v.cmeConfig.toroidalHeight.$touch()"
                          @input="$v.cmeConfig.toroidalHeight.$touch()"
                          hint="Toroidal height"
                          label="Toroidal height"
                          suffix="Rs"
                          type="number"
                          v-model="cmeConfig.toroidalHeight"
                        ></v-text-field>
                      </v-flex>
                      <v-flex class="px-2" md6 v-if="cmeConfig.model === 'LFFSpheromak'" xs12>
                        <v-text-field
                          :error-messages="errorMessages($v.cmeConfig.radius)"
                          @blur="$v.cmeConfig.radius.$touch()"
                          @input="$v.cmeConfig.radius.$touch()"
                          hint="Radius of the spheromak"
                          label="Radius"
                          suffix="Rsun"
                          type="number"
                          v-model="cmeConfig.radius"
                        ></v-text-field>
                      </v-flex>
                      <v-flex class="px-2" md6 v-if="['Cone', 'FRi3D'].includes(cmeConfig.model)" xs12>
                        <v-text-field
                          :error-messages="errorMessages($v.cmeConfig.halfWidth)"
                          @blur="$v.cmeConfig.halfWidth.$touch()"
                          @input="$v.cmeConfig.halfWidth.$touch()"
                          hint="Angular half width"
                          label="Half width"
                          suffix="deg"
                          type="number"
                          v-model="cmeConfig.halfWidth"
                        ></v-text-field>
                      </v-flex>
                      <v-flex class="px-2" md6 v-if="cmeConfig.model === 'FRi3D'" xs12>
                        <v-text-field
                          :error-messages="errorMessages($v.cmeConfig.halfHeight)"
                          @blur="$v.cmeConfig.halfHeight.$touch()"
                          @input="$v.cmeConfig.halfHeight.$touch()"
                          hint="Angular half height"
                          label="Half height"
                          suffix="deg"
                          type="number"
                          v-model="cmeConfig.halfHeight"
                        ></v-text-field>
                      </v-flex>
                      <v-flex class="px-2" md6 v-if="['LFFSpheromak', 'FRi3D'].includes(cmeConfig.model)" xs12>
                        <v-text-field
                          :error-messages="errorMessages($v.cmeConfig.tilt)"
                          @blur="$v.cmeConfig.tilt.$touch()"
                          @input="$v.cmeConfig.tilt.$touch()"
                          label="Tilt angle"
                          suffix="deg"
                          type="number"
                          v-model="cmeConfig.tilt"
                        ></v-text-field>
                      </v-flex>
                      <v-flex class="px-2" md6 v-if="cmeConfig.model === 'FRi3D'" xs12>
                        <v-text-field
                          :error-messages="errorMessages($v.cmeConfig.flattening)"
                          @blur="$v.cmeConfig.flattening.$touch()"
                          @input="$v.cmeConfig.flattening.$touch()"
                          label="Flattening"
                          type="number"
                          v-model="cmeConfig.flattening"
                        ></v-text-field>
                      </v-flex>
                      <v-flex class="px-2" md6 v-if="cmeConfig.model === 'FRi3D'" xs12>
                        <v-text-field
                          :error-messages="errorMessages($v.cmeConfig.pancaking)"
                          @blur="$v.cmeConfig.pancaking.$touch()"
                          @input="$v.cmeConfig.pancaking.$touch()"
                          label="Pancaking"
                          type="number"
                          v-model="cmeConfig.pancaking"
                        ></v-text-field>
                      </v-flex>
                      <v-flex class="px-2" md6 v-if="cmeConfig.model === 'FRi3D'" xs12>
                        <v-text-field
                          :error-messages="errorMessages($v.cmeConfig.twist)"
                          @blur="$v.cmeConfig.twist.$touch()"
                          @input="$v.cmeConfig.twist.$touch()"
                          label="Twist"
                          type="number"
                          v-model="cmeConfig.twist"
                        ></v-text-field>
                      </v-flex>
                      <v-flex class="px-2" md6 v-if="['LFFSpheromak', 'FRi3D'].includes(cmeConfig.model)" xs12>
                        <v-text-field
                          :error-messages="errorMessages($v.cmeConfig.flux)"
                          @blur="$v.cmeConfig.flux.$touch()"
                          @input="$v.cmeConfig.flux.$touch()"
                          hint="Magnetic flux"
                          label="Flux"
                          step="1e13"
                          suffix="Wb"
                          type="number"
                          v-model="cmeConfig.flux"
                        ></v-text-field>
                      </v-flex>
                      <v-flex class="px-2" md6 v-if="cmeConfig.model === 'FRi3D'" xs12>
                        <v-select :items="cmePolarity" label="Polarity" v-model="cmeConfig.polarity"></v-select>
                      </v-flex>
                      <v-flex class="px-2" md6 v-if="['LFFSpheromak', 'FRi3D'].includes(cmeConfig.model)" xs12>
                        <v-select :items="cmeHandedness" label="Handedness" v-model="cmeConfig.chirality"></v-select>
                      </v-flex>
                      <v-flex class="px-2" md6 xs12>
                        <v-text-field
                          :error-messages="errorMessages($v.cmeConfig.density)"
                          @blur="$v.cmeConfig.density.$touch()"
                          @input="$v.cmeConfig.density.$touch()"
                          hint="Number density"
                          label="Density"
                          step="1e-18"
                          suffix="1/cm3"
                          type="number"
                          v-model="cmeConfig.density"
                        ></v-text-field>
                      </v-flex>
                      <v-flex class="px-2" md6 xs12>
                        <v-text-field
                          :error-messages="errorMessages($v.cmeConfig.temperature)"
                          @blur="$v.cmeConfig.temperature.$touch()"
                          @input="$v.cmeConfig.temperature.$touch()"
                          label="Temperature"
                          step="0.1e6"
                          suffix="K"
                          type="number"
                          v-model="cmeConfig.temperature"
                        ></v-text-field>
                      </v-flex>
                    </v-layout>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn @click="closeCme" flat>Cancel</v-btn>
                  <v-btn @click="saveCme" color="info">Save</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <v-dialog :fullscreen="$vuetify.breakpoint.smAndDown" max-width="600px" v-model="dialogDonki">
              <v-card>
                <v-card-title class="px-3">
                  <span class="headline">Add CME from DONKI</span>
                </v-card-title>

                <v-card-text class="px-1">
                  <v-container class="pa-0" grid-list-md>
                    <v-layout wrap>
                      <v-flex class="px-3" md6 xs12>
                        <v-dialog
                          full-width
                          persistent
                          ref="dialog-donki-start-date"
                          v-model="modalDonkiStartDate"
                          width="290px"
                        >
                          <v-text-field label="Start Date" readonly slot="activator" v-model="donkiStartDate"></v-text-field>
                          <v-date-picker
                            @input="$refs['dialog-donki-start-date'].save(donkiStartDate)"
                            scrollable
                            v-model="donkiStartDate"
                          ></v-date-picker>
                        </v-dialog>
                      </v-flex>
                      <v-flex class="px-3" md6 xs12>
                        <v-dialog
                          full-width
                          persistent
                          ref="dialog-donki-end-date"
                          v-model="modalDonkiEndDate"
                          width="290px"
                        >
                          <v-text-field label="End Date" readonly slot="activator" v-model="donkiEndDate"></v-text-field>
                          <v-date-picker
                            @input="$refs['dialog-donki-end-date'].save(donkiEndDate)"
                            scrollable
                            v-model="donkiEndDate"
                          ></v-date-picker>
                        </v-dialog>
                      </v-flex>
                      <v-flex class="px-0">
                        <v-data-table
                          :headers="donkiHeaders"
                          :items="donkiCmes"
                          :loading="$apollo.queries.donkiCmes.loading"
                          :pagination.sync="donkiPagination"
                          item-key="datetime"
                          select-all
                          v-model="selected"
                        >
                          <template v-slot:items="props">
                            <td>
                              <v-checkbox hide-details primary v-model="props.selected"></v-checkbox>
                            </td>
                            <td class="text-no-wrap">{{ props.item.datetime | moment("YYYY-MM-DD HH:mm") }}</td>
                            <td>{{ props.item.speed }}</td>
                            <td>{{ props.item.latitude }}</td>
                            <td>{{ props.item.longitude }}</td>
                          </template>
                        </v-data-table>
                      </v-flex>
                    </v-layout>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn @click="dialogDonki=false" flat>Cancel</v-btn>
                  <v-btn @click="addDonkiCmes(selected)" color="info">Add Selected</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import moment from 'moment';
import { required, between, minValue } from 'vuelidate/lib/validators';
import runMixin from '@/mixins/run';
import defaultConfigCone from '@/config.run.cone';
import defaultConfigSpheromak from '@/config.run.spheromak';
import defaultConfigFRi3D from '@/config.run.fri3d';
import { DONKI_CMES } from '@/graphql.gql';

export default {
  mixins: [runMixin],
  data() {
    return {
      on: false,
      cmeHeaders: [
        {
          text: 'Datetime',
          align: 'left',
          sortable: true,
          value: 'datetime',
        },
        {
          text: 'Model',
          align: 'left',
          sortable: true,
          value: 'model',
        },
        {
          text: 'Speed',
          align: 'left',
          sortable: true,
          value: 'speed',
        },
        {
          text: 'Latitude',
          align: 'left',
          sortable: true,
          value: 'latitude',
        },
        {
          text: 'Longitude',
          align: 'left',
          sortable: true,
          value: 'longitude',
        },
        {
          text: 'Actions',
          align: 'right',
          sortable: false,
          value: 'name',
        },
      ],
      cmePagination: {
        rowsPerPage: -1,
        sortBy: 'datetime',
        descending: true,
      },
      speedDialAddCme: false,
      dialogCme: false,
      cmeIndex: -1,
      cmeConfig: {},
      cmeModels: [
        {
          text: 'Cone',
          value: 'Cone',
        },
        {
          text: 'Spheromak',
          value: 'LFFSpheromak',
        },
        {
          text: 'FRi3D',
          value: 'FRi3D',
        },
      ],
      cmeHandedness: [
        {
          text: 'Right-handed',
          value: 1,
        },
        {
          text: 'Left-handed',
          value: -1,
        },
      ],
      cmePolarity: [
        {
          text: 'East-to-west',
          value: 1,
        },
        {
          text: 'West-to-east',
          value: -1,
        },
      ],
      modalCmeDate: false,
      modalCmeTime: false,
      dialogDonki: false,
      donkiCmes: [],
      donkiHeaders: [
        {
          text: 'Datetime',
          align: 'left',
          sortable: true,
          value: 'datetime',
        },
        {
          text: 'Speed',
          align: 'left',
          sortable: true,
          value: 'speed',
        },
        {
          text: 'Latitude',
          align: 'left',
          sortable: true,
          value: 'latitude',
        },
        {
          text: 'Longitude',
          align: 'left',
          sortable: true,
          value: 'longitude',
        },
      ],
      donkiPagination: {
        rowsPerPage: -1,
        sortBy: 'datetime',
        descending: true,
      },
      selected: [],
      modalDonkiStartDate: false,
      modalDonkiEndDate: false,
      donkiStartDate: moment
        .utc()
        .subtract(1, 'months')
        .format('YYYY-MM-DD'),
      donkiEndDate: moment.utc().format('YYYY-MM-DD'),
    };
  },
  validations: {
    cmeConfig: {
      speed: {
        required,
        between: between(400, 3500),
      },
      latitude: {
        required,
        between: between(-60, 60),
      },
      longitude: {
        required,
        between: between(-180, 180),
      },
      radius: {
        required,
        between: between(5, 30),
      },
      toroidalHeight: {
        required,
        between: between(5, 20),
      },
      halfWidth: {
        required,
        between: between(10, 75),
      },
      halfHeight: {
        required,
        between: between(5, 60),
      },
      tilt: {
        required,
        between: between(-180, 180),
      },
      flattening: {
        required,
        between: between(0.2, 0.8),
      },
      pancaking: {
        required,
        between: between(0, 1),
      },
      twist: {
        required,
        minValue: minValue(0),
      },
      density: {
        required,
        between: between(1e-20, 1e-17),
      },
      temperature: {
        required,
        between: between(1e5, 5e6),
      },
      flux: {
        required,
        between: between(-5e14, 5e14),
      },
    },
    coneGroup: [
      'cmeConfig.latitude',
      'cmeConfig.longitude',
      'cmeConfig.halfWidth',
      'cmeConfig.speed',
      'cmeConfig.density',
      'cmeConfig.temperature',
    ],
    spheromakGroup: [
      'cmeConfig.latitude',
      'cmeConfig.longitude',
      'cmeConfig.speed',
      'cmeConfig.density',
      'cmeConfig.temperature',
      'cmeConfig.radius',
      'cmeConfig.tilt',
      'cmeConfig.flux',
    ],
    fri3dGroup: [
      'cmeConfig.speed',
      'cmeConfig.latitude',
      'cmeConfig.longitude',
      'cmeConfig.toroidalHeight',
      'cmeConfig.halfWidth',
      'cmeConfig.halfHeight',
      'cmeConfig.tilt',
      'cmeConfig.flattening',
      'cmeConfig.pancaking',
      'cmeConfig.flux',
      'cmeConfig.twist',
      'cmeConfig.density',
      'cmeConfig.temperature',
    ],
  },
  watch: {
    dialogDonki(newFlag) {
      if (newFlag) {
        this.donkiStartDate = moment(this.myRun.config.coronal_model.Magnetogram.date)
          .subtract(5, 'days')
          .format('YYYY-MM-DD');
        this.donkiEndDate = moment(this.myRun.config.coronal_model.Magnetogram.date)
          .add(5, 'days')
          .format('YYYY-MM-DD');
      }
    },
  },
  computed: {
    cmeDate: {
      get() {
        return moment.utc(this.cmeConfig.datetime).format('YYYY-MM-DD');
      },
      set(value) {
        this.cmeConfig.datetime = `${value}T${moment.utc(this.cmeConfig.datetime).format('HH:mm')}:00`;
      },
    },
    cmeTime: {
      get() {
        return moment.utc(this.cmeConfig.datetime).format('HH:mm');
      },
      set(value) {
        this.cmeConfig.datetime = `${moment.utc(this.cmeConfig.datetime).format('YYYY-MM-DD')}T${value}:00`;
      },
    },
  },
  apollo: {
    donkiCmes: {
      query: DONKI_CMES,
      variables() {
        return { startDate: this.donkiStartDate, endDate: this.donkiEndDate };
      },
    },
  },
  methods: {
    createCme() {
      this.cmeIndex = -1;
      this.cmeConfig = Object.assign({}, defaultConfigCone);
      this.dialogCme = true;
    },
    editCme(item) {
      this.cmeIndex = this.myRun.config.heliospheric_model.cmes.indexOf(item);
      this.cmeConfig = Object.assign({}, this.myRun.config.heliospheric_model.cmes[this.cmeIndex]);
      this.dialogCme = true;
    },
    deleteCme(item) {
      const index = this.myRun.config.heliospheric_model.cmes.indexOf(item);
      this.myRun.config.heliospheric_model.cmes.splice(index, 1);
    },
    modifyCmeModel(model) {
      if (model === 'Cone') {
        const newCmeConfig = Object.assign({}, defaultConfigCone);
        Object.keys(newCmeConfig).forEach((key) => {
          if (key in this.cmeConfig && key !== 'model') {
            newCmeConfig[key] = this.cmeConfig[key];
          }
        });
        this.cmeConfig = Object.assign({}, newCmeConfig);
      } else if (model === 'LFFSpheromak') {
        const newCmeConfig = Object.assign({}, defaultConfigSpheromak);
        Object.keys(newCmeConfig).forEach((key) => {
          if (key in this.cmeConfig && key !== 'model') {
            newCmeConfig[key] = this.cmeConfig[key];
          }
        });
        this.cmeConfig = Object.assign({}, newCmeConfig);
      } else if (model === 'FRi3D') {
        const newCmeConfig = Object.assign({}, defaultConfigFRi3D);
        Object.keys(newCmeConfig).forEach((key) => {
          if (key in this.cmeConfig && key !== 'model') {
            newCmeConfig[key] = this.cmeConfig[key];
          }
        });
        this.cmeConfig = Object.assign({}, newCmeConfig);
      }
    },
    closeCme() {
      this.dialogCme = false;
      this.cmeIndex = -1;
      this.cmeConfig = {};
    },
    saveCme() {
      if (
        (this.cmeConfig.model === 'Cone' && this.$v.coneGroup.$invalid) ||
        (this.cmeConfig.model === 'LFFSpheromak' && this.$v.spheromakGroup.$invalid) ||
        (this.cmeConfig.model === 'FRi3D' && this.$v.fri3dGroup.$invalid)
      ) {
        this.$snackbar.show('Please, fill the form correctly.');
        return;
      }
      if (this.cmeIndex === -1) {
        this.myRun.config.heliospheric_model.cmes.push(this.cmeConfig);
      } else {
        this.$set(this.myRun.config.heliospheric_model.cmes, this.cmeIndex, Object.assign({}, this.cmeConfig));
      }
      this.dialogCme = false;
    },
    addDonkiCmes(cmes) {
      for (let i = 0; i < cmes.length; i++) {
        const cme = defaultConfigCone;
        cme.datetime = cmes[i].datetime;
        cme.latitude = cmes[i].latitude;
        cme.longitude = cmes[i].longitude;
        cme.halfWidth = cmes[i].halfwidth;
        cme.speed = cmes[i].speed;
        this.myRun.config.heliospheric_model.cmes.push(Object.assign({}, cme));
      }
      this.dialogDonki = false;
    },
  },
};
</script>
